import {
  IdleTimeoutModal,
  NotAuthorized,
  NotFound,
  useUserContext
} from '@threatadvisor/react-components';
import { Space, Spin, Typography } from 'antd';
import { ClientContextProvider } from 'ClientContext';
import React, { ReactElement, Suspense } from 'react';
import { RouteObject, useLocation, useRoutes } from 'react-router-dom';
import { locations, TARoute } from 'SiteMap';

import { ReportPage } from '@threatadvisor/securityevent-components';
import Menu from './Menu';

export const ClientIdContext = React.createContext<number | undefined>(undefined);

function App(): ReactElement {
  const { oidcUser } = useUserContext();

  const location = useLocation();

  const { pathname } = location;

  const hasAccess = (): boolean => {
    if (oidcUser == null) {
      return false;
    }
    const products: string[] = oidcUser['ta:product'];
    return (products ?? []).indexOf('threatadvisor_securityevent_api') > -1;
  };

  const createRoute = (tr: TARoute, parentRoute?: string) => {
    const routeParamString = tr.routeParam ? tr.routeParam.map(r => `/:${r}?`).join('') : '';
    const routeString = tr.route + routeParamString;

    const ri: RouteObject = {
      path: routeString,
      ...(tr.component && { element: tr.component }),
      children: tr.items?.map(c => createRoute(c, routeString))
    };
    return ri;
  };

  const routes: RouteObject[] = [
    {
      path: '/', // Base route includes the ClientContext as well as the Menu for access to clientId routeparam
      element: (
        <ClientContextProvider>
          <Menu isLoading={true} />
        </ClientContextProvider>
      )
    },
    {
      path: '/reports/report',
      element: <ReportPage securityEventApi={import.meta.env.VITE_SECURITYEVENT_API} />
    },
    {
      path: '/:clientId', // Base route includes the ClientContext as well as the Menu for access to clientId routeparam
      element: (
        <ClientContextProvider>
          <Menu accountUrl={`/${pathname.split('/')[1]}/account`} />
        </ClientContextProvider>
      ),
      children: locations // Outlet for children present at --> Menu : TopLevelMenu
        .filter(mi => mi.route !== 'divider')
        .map(mi => createRoute(mi))
        .concat({
          path: '*',
          element: <NotFound />
        })
    }
  ];

  return (
    <Suspense
      fallback={
        <Space
          style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Spin />
          <Typography.Text>Loading...</Typography.Text>
        </Space>
      }
    >
      {useRoutes(
        hasAccess()
          ? [...routes]
          : [
              {
                path: '/',
                element: (
                  <NotAuthorized
                    siteSubject="MDR"
                    redirectTo={import.meta.env.VITE_OIDC_AUTHORITY}
                  />
                )
              }
            ]
      )}
      <IdleTimeoutModal baseUrl={import.meta.env.VITE_OIDC_AUTHORITY} />
    </Suspense>
  );
}

export default App;
